import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import {
  OrganisationInvitationItem,
  organisationInvitationsQuery,
} from "@/app/Organisations/GraphQL/organisationInvitationsQuery";
import { TimeSinceNow } from "@/lib/Components/Common/TimeSinceNow";
import { DeEmphasisedCell } from "@/lib/Components/DataTable/DeEmphasisedCell";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { PlusIcon } from "@heroicons/react/16/solid";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { revokeInvitationMutation } from "@/app/Organisations/GraphQL/organisationInvitationMutations";
import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { Button } from "@/components/catalyst/button";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { InviteOrganizationMemberDialog } from "@/app/Settings/Settings/Components/InviteOrganizationMemberDialog";

type ColumnId = "email" | "invitedBy" | "sent";

const columns: DataTableColDef<OrganisationInvitationItem, any, ColumnId>[] = [
  {
    id: "email",
    accessorFn: (row) => row.email,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Email" />
    ),
  },
  {
    id: "invitedBy",
    accessorFn: (row) => row.invitedBy?.name ?? "--",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Invited by" />
    ),
    cell: DeEmphasisedCell,
  },
  {
    id: "sent",
    accessorFn: (row) => row.created_at,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Sent" />
    ),
    cell: ({ row }) => <TimeSinceNow time={row.original.created_at} />,
  },
];

export function OrganisationInvitationsTable({
  organisationId,
}: {
  organisationId: string;
}) {
  const { mutateAsync: revokeInvitation } = useGqlMutation(
    revokeInvitationMutation,
  );
  const { open: openInviteDialog } = useDialog(InviteOrganizationMemberDialog);

  const getActions: ActionsHook<OrganisationInvitationItem> = (invitation) => [
    {
      label: "Actions",
      items: [
        {
          actions: [
            {
              label: "Revoke",
              Icon: XMarkIcon,
              handleAction: async () => {
                await revokeInvitation({ id: invitation.id });
              },
            },
          ],
        },
      ],
    },
  ];

  return (
    <DataTable
      id="organisation-invitations"
      columns={columns}
      document={organisationInvitationsQuery}
      accessor={(res) => res.organisationInvitations}
      getQueryVariables={({ pagination }) => ({
        organisation_id: organisationId,
        page: pagination.pageIndex,
        first: pagination.pageSize,
      })}
      searchable={false}
      columnToggles={false}
      getActions={getActions}
      rightButtons={
        <Button
          onClick={() => openInviteDialog({ organisation_id: organisationId })}
        >
          <PlusIcon />
          Invite user
        </Button>
      }
    />
  );
}
